import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Building2, Users, Briefcase, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { crmApi, teamApi } from "@/utils/api";

const DEPARTMENTS = ["Guatemala", "Sacatepéquez", "Chimaltenango", "Escuintla", "Santa Rosa", "Sololá", "Quetzaltenango"];

const ContactForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  const [formData, setFormData] = useState({
    contact_type: "buyer",
    name: "",
    email: "",
    phone: "",
    phone_secondary: "",
    address: "",
    city: "",
    department: "",
    company_name: "",
    stage_id: "",
    budget_min: "",
    budget_max: "",
    looking_for: "",
    notes: "",
    next_follow_up: "",
    assigned_to: "",
    source: "",
  });

  const fetchData = useCallback(async () => {
    try {
      const [stagesRes, teamRes] = await Promise.all([
        crmApi.getStages(),
        teamApi.getAll(),
      ]);
      
      setStages(stagesRes.data);
      setTeamMembers(teamRes.data);

      if (isNew && stagesRes.data.length > 0) {
        setFormData(prev => ({ ...prev, stage_id: stagesRes.data[0].id }));
      }

      if (!isNew) {
        const contactRes = await crmApi.getContact(id);
        const contact = contactRes.data;
        setFormData(prev => ({
          ...prev,
          ...contact,
          budget_min: contact.budget_min || "",
          budget_max: contact.budget_max || "",
          next_follow_up: contact.next_follow_up ? contact.next_follow_up.split('T')[0] : "",
        }));
      }
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : 0,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : 0,
        next_follow_up: formData.next_follow_up || null
      };

      if (isNew) {
        await crmApi.createContact(payload);
        toast.success("Contacto creado exitosamente");
      } else {
        await crmApi.updateContact(id, payload);
        toast.success("Contacto actualizado");
      }
      navigate("/admin/crm/contacts");
    } catch (error) {
      toast.error("Error al guardar el contacto");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="contact-form">
        <div className="flex items-center gap-4">
          <Link to="/admin/crm/contacts">
            <Button variant="ghost" size="icon" className="text-[#A3A3A3] hover:text-white">
              <ArrowLeft size={20} />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-white">
              {isNew ? "Nuevo Contacto" : formData.name}
            </h1>
            <p className="text-[#A3A3A3] text-sm">
              {isNew ? "Agrega un nuevo contacto al CRM" : "Edita la información del contacto"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 bg-[#121212] border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-lg">Información Básica</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Tipo de Contacto</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <button type="button" onClick={() => handleChange("contact_type", "seller")}
                      className={`p-4 rounded-lg border flex flex-col items-center gap-2 ${formData.contact_type === "seller" ? "border-emerald-500 bg-emerald-500/10" : "border-white/10"}`}>
                      <Building2 size={24} className={formData.contact_type === "seller" ? "text-emerald-500" : "text-[#A3A3A3]"} />
                      <span className={formData.contact_type === "seller" ? "text-white" : "text-[#A3A3A3]"}>Vendedor</span>
                    </button>
                    <button type="button" onClick={() => handleChange("contact_type", "buyer")}
                      className={`p-4 rounded-lg border flex flex-col items-center gap-2 ${formData.contact_type === "buyer" ? "border-blue-500 bg-blue-500/10" : "border-white/10"}`}>
                      <Users size={24} className={formData.contact_type === "buyer" ? "text-blue-500" : "text-[#A3A3A3]"} />
                      <span className={formData.contact_type === "buyer" ? "text-white" : "text-[#A3A3A3]"}>Comprador</span>
                    </button>
                    <button type="button" onClick={() => handleChange("contact_type", "broker")}
                      className={`p-4 rounded-lg border flex flex-col items-center gap-2 ${formData.contact_type === "broker" ? "border-purple-500 bg-purple-500/10" : "border-white/10"}`}>
                      <Briefcase size={24} className={formData.contact_type === "broker" ? "text-purple-500" : "text-[#A3A3A3]"} />
                      <span className={formData.contact_type === "broker" ? "text-white" : "text-[#A3A3A3]"}>Corredor</span>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Nombre Completo *</Label>
                    <Input value={formData.name} onChange={(e) => handleChange("name", e.target.value)}
                      className="bg-[#1a1a1a] border-white/10 text-white" required data-testid="contact-name" />
                  </div>
                  {formData.contact_type === "broker" && (
                    <div className="space-y-2">
                      <Label className="text-white">Empresa</Label>
                      <Input value={formData.company_name} onChange={(e) => handleChange("company_name", e.target.value)}
                        className="bg-[#1a1a1a] border-white/10 text-white" />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Teléfono Principal *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
                      <Input value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)}
                        className="pl-10 bg-[#1a1a1a] border-white/10 text-white" required data-testid="contact-phone" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
                      <Input type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)}
                        className="pl-10 bg-[#1a1a1a] border-white/10 text-white" data-testid="contact-email" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Departamento</Label>
                    <Select value={formData.department} onValueChange={(v) => handleChange("department", v)}>
                      <SelectTrigger className="bg-[#1a1a1a] border-white/10 text-white">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {DEPARTMENTS.map(d => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Ciudad</Label>
                    <Input value={formData.city} onChange={(e) => handleChange("city", e.target.value)}
                      className="bg-[#1a1a1a] border-white/10 text-white" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Notas</Label>
                  <Textarea value={formData.notes} onChange={(e) => handleChange("notes", e.target.value)}
                    className="bg-[#1a1a1a] border-white/10 text-white min-h-[100px]" placeholder="Notas..." />
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card className="bg-[#121212] border-white/5">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Estado</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Etapa</Label>
                    <Select value={formData.stage_id} onValueChange={(v) => handleChange("stage_id", v)}>
                      <SelectTrigger className="bg-[#1a1a1a] border-white/10 text-white" data-testid="contact-stage">
                        <SelectValue placeholder="Seleccionar etapa" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        {stages.map(s => (
                          <SelectItem key={s.id} value={s.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                              {s.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Asignado a</Label>
                    <Select value={formData.assigned_to || "unassigned"} onValueChange={(v) => handleChange("assigned_to", v === "unassigned" ? "" : v)}>
                      <SelectTrigger className="bg-[#1a1a1a] border-white/10 text-white">
                        <SelectValue placeholder="Sin asignar" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="unassigned">Sin asignar</SelectItem>
                        {teamMembers.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Próximo Seguimiento</Label>
                    <Input type="date" value={formData.next_follow_up} onChange={(e) => handleChange("next_follow_up", e.target.value)}
                      className="bg-[#1a1a1a] border-white/10 text-white" />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Fuente</Label>
                    <Select value={formData.source} onValueChange={(v) => handleChange("source", v)}>
                      <SelectTrigger className="bg-[#1a1a1a] border-white/10 text-white">
                        <SelectValue placeholder="¿Cómo nos contactó?" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="website">Sitio Web</SelectItem>
                        <SelectItem value="referral">Referido</SelectItem>
                        <SelectItem value="social">Redes Sociales</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="phone">Llamada</SelectItem>
                        <SelectItem value="other">Otro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {formData.contact_type === "buyer" && (
                <Card className="bg-[#121212] border-white/5">
                  <CardHeader>
                    <CardTitle className="text-white text-lg">Presupuesto</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-white">Busca</Label>
                      <Select value={formData.looking_for} onValueChange={(v) => handleChange("looking_for", v)}>
                        <SelectTrigger className="bg-[#1a1a1a] border-white/10 text-white">
                          <SelectValue placeholder="Comprar o Alquilar" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          <SelectItem value="Venta">Comprar</SelectItem>
                          <SelectItem value="Alquiler">Alquilar</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-white text-sm">Mín (USD)</Label>
                        <Input type="number" value={formData.budget_min} onChange={(e) => handleChange("budget_min", e.target.value)}
                          className="bg-[#1a1a1a] border-white/10 text-white" placeholder="0" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white text-sm">Máx (USD)</Label>
                        <Input type="number" value={formData.budget_max} onChange={(e) => handleChange("budget_max", e.target.value)}
                          className="bg-[#1a1a1a] border-white/10 text-white" placeholder="0" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6 pb-20">
            <Link to="/admin/crm/contacts">
              <Button type="button" variant="outline" className="border-white/10 text-white">Cancelar</Button>
            </Link>
            <Button type="submit" className="bg-[#D4AF37] text-black hover:bg-[#FFE082]" disabled={saving} data-testid="save-contact-btn">
              <Save size={18} className="mr-2" />
              {saving ? "Guardando..." : (isNew ? "Crear Contacto" : "Guardar")}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default ContactForm;
